import { getGlobalHeadersAsObject } from "../global-headers";

export type PersonActivityType = "call" | "meeting" | "job_application";
export type CallDirection = "inbound" | "outbound";
export type CallOutcome = "connected" | "voicemail" | "no_answer" | "busy";
export type MeetingType = "video" | "in_person";

export type PersonActivity = {
	id: number;
	person: number;
	/** Null for system-logged entries (job applications). */
	user: number | null;
	user_name: string;
	user_first_name: string;
	activity_type: PersonActivityType;
	content: string;
	happened_at: string;
	created: string;
	updated: string;
	call_direction?: CallDirection;
	call_outcome?: CallOutcome;
	meeting_type?: MeetingType;
	duration_minutes?: number;
};

export type PersonActivityCreate = {
	activity_type: PersonActivityType;
	content?: string;
	happened_at: string;
	call_direction?: CallDirection;
	call_outcome?: CallOutcome;
	meeting_type?: MeetingType;
	duration_minutes?: number;
};

export const getPersonActivities = (personId: number): Promise<PersonActivity[]> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`getPersonActivities called with invalid argument id '${personId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/activities/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const createPersonActivity = (
	personId: number,
	data: PersonActivityCreate
): Promise<PersonActivity> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`createPersonActivity called with invalid argument id '${personId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/activities/`, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	}).then((res) => res.json());
};

export const updatePersonActivity = (
	personId: number,
	activityId: number,
	data: Partial<PersonActivityCreate>
): Promise<PersonActivity> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`updatePersonActivity called with invalid personId '${personId}'. Expected number`
		);
	}
	if (!activityId || typeof activityId !== "number") {
		throw new Error(
			`updatePersonActivity called with invalid activityId '${activityId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/activities/${activityId}/`, {
		method: "PUT",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	}).then((res) => res.json());
};

export const deletePersonActivity = (
	personId: number,
	activityId: number
): Promise<void> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`deletePersonActivity called with invalid personId '${personId}'. Expected number`
		);
	}
	if (!activityId || typeof activityId !== "number") {
		throw new Error(
			`deletePersonActivity called with invalid activityId '${activityId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/activities/${activityId}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	}).then(() => undefined);
};
